import { useEffect, useState } from "react";
import { Loader2, FileText, Download } from "lucide-react";
import DashboardCharts from "./charts";
import { BASE_URL, API_ENDPOINTS } from "../../config/api.config";

// ── CSV helpers ────────────────────────────────────────────────────────────────

function toCSV(headers, rows) {
  const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [headers.join(","), ...rows.map((r) => r.map(escape).join(","))].join("\n");
}

function triggerDownload(csv, filename) {
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function currentMonthValue() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

// ── Per-report download configs ────────────────────────────────────────────────

const INQUIRY_TYPES = [
  { value: "admin",    label: "Admin Inquiry" },
  { value: "event",    label: "Event Engagements" },
  { value: "resource", label: "Resource Engagements" },
];

// ── Main component ─────────────────────────────────────────────────────────────

const MainAnalyticsReport = () => {
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Per-report filter state
  const [eventsMonth, setEventsMonth] = useState(currentMonthValue());
  const [inquiryType, setInquiryType] = useState("admin");

  // Per-report downloading state
  const [downloading, setDownloading] = useState({});

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) { setIsLoading(false); return; }
    fetch(`${BASE_URL}${API_ENDPOINTS.SUPERADMIN_DASHBOARD_ANALYTICS}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((result) => { if (result) setAnalytics(result?.data ?? result); })
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, []);

  const setDL = (id, val) => setDownloading((prev) => ({ ...prev, [id]: val }));

  const fetchJSON = async (url) => {
    const token = localStorage.getItem("accessToken");
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const result = await res.json();
    return result?.data ?? result;
  };

  // ── Download handlers ──────────────────────────────────────────────────────

  const downloadNonprofitMatrix = async () => {
    setDL("nonprofit-matrix", true);
    try {
      const data = await fetchJSON(`${BASE_URL}${API_ENDPOINTS.REPORT_NONPROFIT_MATRIX}`);
      const rows = data.map((r) => [
        r.organizationName, r.email, r.phone, r.address, r.zipCode, r.ein,
        r.status, r.keyServices, r.populationsServed, r.sources, r.registrationDate,
      ]);
      triggerDownload(toCSV(
        ["Organization Name","Email","Phone","Address","ZIP","EIN","Status","Key Services","Populations Served","Sources","Registration Date"],
        rows,
      ), "nonprofit-matrix.csv");
    } catch { /* silently ignore */ }
    finally { setDL("nonprofit-matrix", false); }
  };

  const downloadEventsMonthly = async () => {
    setDL("events-monthly", true);
    try {
      const data = await fetchJSON(`${BASE_URL}${API_ENDPOINTS.REPORT_EVENTS_MONTHLY}?month=${eventsMonth}`);
      // Summary row + org breakdown
      const rows = [
        ["SUMMARY", "", "", ""],
        ["Month", data.month, "", ""],
        ["Total Created", data.totalCreated, "", ""],
        ["Upcoming", data.totalUpcoming, "", ""],
        ["Past", data.totalPast, "", ""],
        ["Flagged", data.flaggedCount, "", ""],
        ["", "", "", ""],
        ["BY ORGANIZATION", "", "", ""],
        ...( data.byOrganization ?? []).map((r) => [r.orgName, r.eventsCreated, r.upcoming, r.past]),
      ];
      triggerDownload(toCSV(["Organization","Events Created","Upcoming","Past"], rows), `events-monthly-${eventsMonth}.csv`);
    } catch { /* silently ignore */ }
    finally { setDL("events-monthly", false); }
  };

  const downloadFlaggedEvents = async () => {
    setDL("flagged-events", true);
    try {
      const data = await fetchJSON(`${BASE_URL}${API_ENDPOINTS.REPORT_FLAGGED_EVENTS}`);
      const rows = data.map((r) => [
        r.eventTitle, r.flaggedByOrg, r.flaggedByEmail, r.reason,
        r.decision, fmtDate(r.decidedAt), fmtDate(r.flaggedOn),
      ]);
      triggerDownload(toCSV(
        ["Event","Flagged By (Org)","Flagged By (Email)","Reason","Decision","Decision Date","Flagged On"],
        rows,
      ), "flagged-events.csv");
    } catch { /* silently ignore */ }
    finally { setDL("flagged-events", false); }
  };

  const downloadCollaborations = async () => {
    setDL("collaborations", true);
    try {
      const data = await fetchJSON(`${BASE_URL}${API_ENDPOINTS.REPORT_COLLABORATIONS}`);
      const rows = [];
      for (const r of data) {
        for (const rec of r.recipients) {
          if (rec.tasks.length === 0) {
            rows.push([r.fromOrg, r.event, r.resourcesNeeded, rec.toOrg, rec.status, "", "", ""]);
          } else {
            for (const t of rec.tasks) {
              rows.push([r.fromOrg, r.event, r.resourcesNeeded, rec.toOrg, rec.status, t.taskName, t.countNeeded, t.status]);
            }
          }
        }
        if (r.recipients.length === 0) {
          rows.push([r.fromOrg, r.event, r.resourcesNeeded, "", "", "", "", ""]);
        }
      }
      triggerDownload(toCSV(
        ["From Org","Event","Resources Needed","To Org","Collab Status","Task","Count Needed","Task Status"],
        rows,
      ), "collaborations.csv");
    } catch { /* silently ignore */ }
    finally { setDL("collaborations", false); }
  };

  const downloadInquiryEngagement = async () => {
    setDL("inquiry-engagement", true);
    try {
      const data = await fetchJSON(`${BASE_URL}${API_ENDPOINTS.REPORT_INQUIRY_ENGAGEMENT}?type=${inquiryType}`);
      let headers, rows;
      if (inquiryType === "admin") {
        headers = ["Subject","Sender Org","Sender Email","Status","Messages","Created"];
        rows = data.map((r) => [r.subject, r.senderOrg, r.senderEmail, r.status, r.messageCount, fmtDate(r.createdAt)]);
      } else if (inquiryType === "event") {
        headers = ["Event","Organization","Inquiry Count","Latest"];
        rows = data.map((r) => [r.eventTitle, r.orgName, r.count, fmtDate(r.latestAt)]);
      } else {
        headers = ["Resource","Organization","Inquiry Count","Latest"];
        rows = data.map((r) => [r.resourceTitle, r.orgName, r.count, fmtDate(r.latestAt)]);
      }
      triggerDownload(toCSV(headers, rows), `inquiry-engagement-${inquiryType}.csv`);
    } catch { /* silently ignore */ }
    finally { setDL("inquiry-engagement", false); }
  };

  const downloadImpressions = async () => {
    setDL("impressions", true);
    try {
      const data = await fetchJSON(`${BASE_URL}${API_ENDPOINTS.REPORT_IMPRESSIONS}`);
      const orgRows = (data.orgSummary ?? []).map((r) => [r.orgName, r.totalViews, r.totalClicks, "", "", ""]);
      const eventRows = (data.eventDetail ?? []).map((r) => ["", "", "", r.eventTitle, r.orgName, r.viewCount, r.clickCount]);
      triggerDownload(toCSV(
        ["Org Name","Total Views","Total Clicks","Event Title","Event Org","Event Views","Event Clicks"],
        [...orgRows, ["", "", "", "", "", "", ""], ...eventRows],
      ), "impressions.csv");
    } catch { /* silently ignore */ }
    finally { setDL("impressions", false); }
  };

  const DOWNLOAD_HANDLERS = {
    "nonprofit-matrix":   downloadNonprofitMatrix,
    "events-monthly":     downloadEventsMonthly,
    "flagged-events":     downloadFlaggedEvents,
    "collaborations":     downloadCollaborations,
    "inquiry-engagement": downloadInquiryEngagement,
    "impressions":        downloadImpressions,
  };

  // ── Report rows config ─────────────────────────────────────────────────────

  const REPORTS = [
    {
      id: "nonprofit-matrix",
      name: "NonProfit Matrix Excel Sheet",
      description: "All nonprofits with services, populations served, contact info, and status",
    },
    {
      id: "events-monthly",
      name: "Monthly Summary Report (Events)",
      description: "Event counts (total, upcoming, past, flagged) and per-org breakdown",
      filter: (
        <input
          type="month"
          value={eventsMonth}
          onChange={(e) => setEventsMonth(e.target.value)}
          className="mt-2 border border-border rounded-md px-2 py-1 text-xs font-popinsMedium outline-none focus:border-[#3FC4E3] transition-colors"
        />
      ),
    },
    {
      id: "flagged-events",
      name: "Flagged Events",
      description: "All flagged events with reason, decision status, and resolution date",
    },
    {
      id: "collaborations",
      name: "Collaborations (and Resources Requested)",
      description: "Collaboration requests with resources needed and task breakdown per recipient",
    },
    {
      id: "inquiry-engagement",
      name: "Inquiry Engagement",
      description: "Engagement across admin inquiries, event engagements, and resource engagements",
      filter: (
        <div className="flex gap-1.5 mt-2 flex-wrap">
          {INQUIRY_TYPES.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setInquiryType(opt.value)}
              className={`px-2.5 py-0.5 rounded text-xs font-popinsMedium transition-colors border ${
                inquiryType === opt.value
                  ? "bg-[#3FC4E3]/10 border-[#3FC4E3] text-[#3FC4E3]"
                  : "border-border bg-gray-50 text-black/40 hover:text-black/60"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      ),
    },
    {
      id: "impressions",
      name: "Impressions and Clicks",
      description: "View and click data grouped by organization and per event",
    },
  ];

  // ── Summary cards ──────────────────────────────────────────────────────────

  const summaryCards = [
    { label: "New Registrations This Month", value: isLoading ? null : analytics?.registrationsThisMonth ?? "—" },
    { label: "Total Events",                  value: isLoading ? null : analytics?.totalEvents ?? "—" },
    { label: "Flagged Events",                value: isLoading ? null : analytics?.flaggedEventsCount ?? "—" },
    { label: "Active Nonprofits",             value: isLoading ? null : analytics?.activeNonprofits ?? "—" },
  ];

  return (
    <div className="my-6">
      <h1 className="font-popinsSemiBold text-xl lg:text-2xl">Analytics & Reports</h1>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-6">
        {summaryCards.map((item, i) => (
          <div key={i} className="bg-white px-5 py-8 rounded-xl border shadow-md flex flex-col items-start">
            <p className="font-popinsMedium text-sm text-black/60">{item.label}</p>
            {item.value === null ? (
              <Loader2 className="animate-spin text-[#3FC4E3] mt-3" size={20} />
            ) : (
              <p className="text-2xl font-popinsSemiBold mt-2">{item.value.toLocaleString()}</p>
            )}
          </div>
        ))}
      </div>

      {/* Charts */}
      <DashboardCharts registrationsTrend={analytics?.registrationsTrend ?? []} isLoading={isLoading} />

      {/* Reports Listing */}
      <div className="mt-10">
        <h2 className="font-popinsSemiBold text-lg mb-4">Reports</h2>
        <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
          {REPORTS.map((report, i) => (
            <div
              key={report.id}
              className={`flex items-start gap-4 px-6 py-5 ${i < REPORTS.length - 1 ? "border-b border-border" : ""}`}
            >
              <div className="p-2 rounded-lg bg-[#3FC4E3]/10 flex-shrink-0 mt-0.5">
                <FileText size={18} className="text-[#3FC4E3]" />
              </div>

              <div className="flex-1 min-w-0">
                <p className="font-popinsSemiBold text-sm text-black/80">{report.name}</p>
                <p className="font-popinsRegular text-xs text-black/40 mt-0.5">{report.description}</p>
                {report.filter && report.filter}
              </div>

              <button
                onClick={DOWNLOAD_HANDLERS[report.id]}
                disabled={!!downloading[report.id]}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-xs font-popinsMedium text-black/60 hover:border-[#3FC4E3] hover:text-[#3FC4E3] disabled:opacity-50 transition-colors flex-shrink-0 mt-0.5"
              >
                {downloading[report.id] ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Download size={13} />
                )}
                {downloading[report.id] ? "Preparing…" : "Download CSV"}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MainAnalyticsReport;
